import { NextPage } from 'next'
import LabLayout from '../components/LabLayout'
import { ProtectRoute } from '../../../utils/auth/ProtectRoute'
import styles from './_labBonusPage.module.scss'
import Image from 'next/image'
import { t } from '../../../utils'
import classNames from 'classnames'
import { useAuth } from '../../../utils/auth'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { APPLY_LAB_PLUS_BENEFIT_MUTATION } from '../../../gql/mutations'
import { User } from '../../../__generated__/graphql'
import { labBonusSuccessPath, labPath } from '../../../utils/routeHelpers'
import { CURRENT_USER_QUERY } from '../../../gql/queries'
import { getOperationName } from 'apollo-boost'
import { SplashScreenPure } from '../../../components/SplashScreen'
import { useTeamMemberLabPlusGuard } from '../../../utils/hooks/useTeamMemberLabPlusGuard'

const labBonusBanner = require('@public/images/lab/lab_bonus_banner.png')
const leftTestimonialImage = require('@public/images/lab/testimonial_debbie.jpg')
const rightTestimonialImage = require('@public/images/lab/testimonial_mark.jpg')

const smallPlans = ['None', 'Gifted Solo Plan', 'Solo', 'Small Team']

export const isOnSmallPlan = (user: User) =>
  smallPlans.includes(user?.currentPlan?.name)

const Testimonial: React.FC<{
  testimonialImage: string
  testimonialText: string
  testimonialAuthor: string
}> = ({ testimonialImage, testimonialText, testimonialAuthor }) => {
  return (
    <div className={styles.testimonial}>
      <Image
        src={testimonialImage}
        alt={t('Testimonial')}
        className={styles.testimonialImage}
        width={237}
        height={237}
      />
      <div className={styles.quote}>
        <p>{t(testimonialText)}</p>
        <strong>{testimonialAuthor}</strong>
      </div>
    </div>
  )
}

const BonusSection: React.FC<{
  goToNextPage: () => void
  onApplyBenefitStart: () => void
  onApplyBenefitError: () => void
}> = ({ goToNextPage, onApplyBenefitStart, onApplyBenefitError }) => {
  const { currentUser } = useAuth()
  const router = useRouter()

  const [applyBenefit, { loading }] = useMutation(
    APPLY_LAB_PLUS_BENEFIT_MUTATION,
    {
      onCompleted: () => router.push(labBonusSuccessPath()),
      onError: onApplyBenefitError,
      refetchQueries: [getOperationName(CURRENT_USER_QUERY)],
      awaitRefetchQueries: true
    }
  )

  const isSmallPlan = isOnSmallPlan(currentUser)

  return (
    <section className={styles.bonusSection}>
      <h1>{t('We have a gift for you!')}</h1>
      <h2>
        {t('Activate Your')}{' '}
        <span>{isSmallPlan ? t('free') : t('$177 Discount')}</span>{' '}
        {!isSmallPlan && t('For Your ')}
        {t('Worship Online Subscription')}
      </h2>
      <p>
        {isSmallPlan
          ? 'Your Lab+ subscription gives you a free 3-month trial to a 10-person Medium Team Plan ($177 value!) for Worship Online song tutorials! Would you like to activate that trial?'
          : 'Your Lab+ subscription gives you a free 3-month trial to a 10-person Medium Team Plan ($177 value!) for Worship Online song tutorials! Since you are already on this plan (or larger plan), we are adding a $177 credit to your account!'}
      </p>
      {isSmallPlan && (
        <p>
          <em>
            {t('(Auto-renews at $59/mo after 3 months unless canceled).')}
          </em>
        </p>
      )}
      <div className={styles.buttons}>
        <button
          className={classNames(styles.activateButton)}
          onClick={() => {
            onApplyBenefitStart()
            applyBenefit()
          }}
          disabled={loading}
        >
          {isSmallPlan
            ? t('Activate my free trial!')
            : t('Activate my $177 credit!')}
        </button>
        <button className={styles.cancelButton} onClick={goToNextPage}>
          {t('Not right now')}
        </button>
        <em>{t('You can activate this bonus at any time')}</em>
      </div>
    </section>
  )
}

const LabBonus: NextPage & {
  theme?: string
} = () => {
  const router = useRouter()
  const { currentUser, isLoading } = useAuth()
  const {
    isBlocked: isTeamMemberBlocked,
    isLoading: isTeamMemberCheckLoading
  } = useTeamMemberLabPlusGuard()
  const [isApplyingBenefit, setIsApplyingBenefit] = useState(false)
  const isForNewUser = 'welcome' in router.query

  const goNextPage = () => {
    router.push(isForNewUser ? '/welcome' : labPath())
  }

  useEffect(() => {
    if (!router.isReady || isApplyingBenefit || isTeamMemberCheckLoading) return

    if (currentUser?.labPlusBenefitConsumedAt) {
      router.replace(labBonusSuccessPath())
      return
    }

    if (!isLoading && currentUser && !currentUser.currentPlan) {
      goNextPage()
    }
  }, [
    currentUser,
    currentUser?.labPlusBenefitConsumedAt,
    isApplyingBenefit,
    isLoading,
    isTeamMemberCheckLoading,
    isForNewUser,
    router,
    router.isReady
  ])

  if (isLoading || isTeamMemberCheckLoading) {
    return <SplashScreenPure />
  }

  if (isTeamMemberBlocked) {
    return null
  }

  return (
    <LabLayout>
      <div className={styles.container}>
        <Image
          src={labBonusBanner}
          alt={t('Lab Bonus')}
          width={1513}
          height={395}
          className={styles.labBonusBanner}
        />
        <div className={styles.contentContainer}>
          <Testimonial
            testimonialImage={leftTestimonialImage}
            testimonialText={t(
              "Worship online is amazing! It keeps our whole team on the same page! Don't know what I'd do without it!"
            )}
            testimonialAuthor={t('Debbie')}
          />
          <BonusSection
            goToNextPage={goNextPage}
            onApplyBenefitStart={() => setIsApplyingBenefit(true)}
            onApplyBenefitError={() => setIsApplyingBenefit(false)}
          />
          <Testimonial
            testimonialImage={rightTestimonialImage}
            testimonialText={t(
              'Our rehearsals are so much smoother now that everyone learns their exact part in advance.'
            )}
            testimonialAuthor={t('Mark')}
          />
        </div>
      </div>
    </LabLayout>
  )
}

LabBonus.theme = 'dark'

export default ProtectRoute(LabBonus, { isLabPage: true })
